import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
  Paper,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

// Feature flag definitions with descriptions
const FEATURE_FLAG_DEFINITIONS = {
  usag_import_enabled: {
    label: 'USAG Import',
    description: 'Allow importing athlete, coach, and club data from USAG sanction files',
    category: 'Data'
  },
  public_scoreboard_enabled: {
    label: 'Public Scoreboard',
    description: 'Enable public-facing live scoreboards for competitions',
    category: 'Display'
  },
  team_registration_enabled: {
    label: 'Team Registration',
    description: 'Allow teams to register for competitions through the system',
    category: 'Registration'
  },
  athlete_self_registration_enabled: {
    label: 'Athlete Self-Registration',
    description: 'Allow athletes to register themselves for competitions',
    category: 'Registration'
  }
};

const FeatureFlagsForm = ({ flags, onSave }) => {
  const [formData, setFormData] = useState({
    usag_import_enabled: true,
    public_scoreboard_enabled: false,
    team_registration_enabled: true,
    athlete_self_registration_enabled: false,
    ...flags
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...flags }));
  }, [flags]);

  const handleChange = (flagName, value) => {
    setFormData(prev => ({ ...prev, [flagName]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  // Group flags by category
  const categories = {};
  Object.entries(FEATURE_FLAG_DEFINITIONS).forEach(([key, def]) => {
    if (!categories[def.category]) {
      categories[def.category] = [];
    }
    categories[def.category].push({ key, ...def });
  });

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Toggle features on or off. Changes take effect immediately after saving.
      </Typography>

      <Stack spacing={2}>
        {Object.entries(categories).map(([category, categoryFlags]) => (
          <Paper key={category} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {category}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              {categoryFlags.map(flag => (
                <Box key={flag.key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData[flag.key] ?? false}
                        onChange={(e) => handleChange(flag.key, e.target.checked)}
                      />
                    }
                    label={flag.label}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ ml: 6, mt: -0.5 }}
                  >
                    {flag.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}

        {/* Handle any flags not in definitions */}
        {Object.keys(formData).filter(key => !FEATURE_FLAG_DEFINITIONS[key]).length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Other
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              {Object.keys(formData)
                .filter(key => !FEATURE_FLAG_DEFINITIONS[key])
                .map(key => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={formData[key] ?? false}
                        onChange={(e) => handleChange(key, e.target.checked)}
                      />
                    }
                    label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  />
                ))}
            </Stack>
          </Paper>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default FeatureFlagsForm;
